import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { BaseClientSettings, IClientSettings } from "@shared/mirage/services/IClientSettingsService";
import { IBaseEntity } from "@shared/mirage/core/contracts/IBaseEntity";
import { MirageApplicationTypes, MirageInternalApplicationTypes } from "@shared/mirage/core/IMirageApplications";
import { PicklistsFile } from "@shared/mirage/PickLists/PicklistFile";
import { SetterOrUpdater } from "recoil";
import { useEventListener } from "./useEventListener";

export type SetValue<T> = Dispatch<SetStateAction<T>>;
declare global {
    interface WindowEventMap {
        "session-storage": CustomEvent;
    }

    interface DocumentEventMap {
        "prolonged-session": CustomEvent;
    }
}

const useSessionStorage = <K extends keyof ISessionStorageMap>(
    key: K,
    initialValue: ISessionStorageMap[K] = {} as ISessionStorageMap[K]
): [ISessionStorageMap[K], SetterOrUpdater<ISessionStorageMap[K]>] => {
    const readValue = useCallback((): ISessionStorageMap[K] => {
        // Prevent build error "window is undefined" but keep keep working
        if (typeof window === "undefined") {
            return initialValue;
        }

        try {
            const item = window.sessionStorage.getItem(key);
            return item ? (JSON.parse(item) as ISessionStorageMap[K]) : initialValue;
        } catch (error) {
            console.warn(`Error reading sessionStorage key “${key}”:`, error);
            return initialValue;
        }
    }, [initialValue, key]);
    const [storedValue, setStoredValue] = useState<ISessionStorageMap[K]>(readValue());

    const setValue: SetValue<ISessionStorageMap[K]> = useCallback(
        (value) => {
            // Prevent build error "window is undefined" but keeps working
            if (typeof window == "undefined") {
                console.warn(`Tried setting sessionStorage key “${key}” even though environment is not a client`);
            }

            try {
                // Allow value to be a function so we have the same API as useState
                const newValue = value instanceof Function ? value(storedValue) : value;

                // Save to session storage
                window.sessionStorage.setItem(key, JSON.stringify(newValue));
                // Save state
                setStoredValue(newValue);

                // We dispatch a custom event so every useSessionStorage hook are notified
                window.dispatchEvent(new StorageEvent("session-storage", { key }));
            } catch (error) {
                console.warn(`Error setting sessionStorage key “${key}”:`, error);
            }
        },
        [key, storedValue]
    );

    useEffect(() => {
        setStoredValue(readValue());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    const handleStorageChange = useCallback(
        (event: StorageEvent | CustomEvent) => {
            if ((event as StorageEvent)?.key && (event as StorageEvent).key !== key) {
                return;
            }
            setStoredValue(readValue());
        },
        [key, readValue]
    );

    useEventListener("storage", handleStorageChange);
    useEventListener("session-storage", handleStorageChange);

    return [storedValue, setValue];
};

export const useSetSessionStorage = <K extends keyof ISessionStorageMap>(key: K): SetterOrUpdater<ISessionStorageMap[K]> => {
    const readValue = useCallback((): ISessionStorageMap[K] => {
        // Prevent build error "window is undefined" but keep keep working
        if (typeof window === "undefined") {
            return {} as ISessionStorageMap[K];
        }

        try {
            const item = window.sessionStorage.getItem(key);
            return item ? (JSON.parse(item) as ISessionStorageMap[K]) : ({} as ISessionStorageMap[K]);
        } catch (error) {
            console.warn(`Error reading sessionStorage key “${key}”:`, error);
            return {} as ISessionStorageMap[K];
        }
    }, [key]);
    const setValue: SetValue<ISessionStorageMap[K]> = useCallback(
        (value) => {
            // Prevent build error "window is undefined" but keeps working
            if (!window) {
                console.warn(`Tried setting sessionStorage key “${key}” even though environment is not a client`);
            }
            const storedValue = readValue();
            try {
                // Allow value to be a function so we have the same API as useState
                const newValue = value instanceof Function ? value(storedValue) : value;

                // Save to session storage
                window.sessionStorage.setItem(key, JSON.stringify(newValue));

                // We dispatch a custom event so every useSessionStorage hook are notified
                window.dispatchEvent(new StorageEvent("session-storage", { key }));
            } catch (error) {
                console.warn(`Error setting sessionStorage key “${key}”:`, error);
            }
        },
        [key, readValue]
    );

    return setValue;
};

export type ResolvedRecord = { [entityName: string]: IBaseEntity | null };
export interface IBcSessionStorageMap {
    templateResolvedRecord: ResolvedRecord;
    clientSettings: IClientSettings;
}
export interface ILcSessionStorageMap {
    templateResolvedRecord: ResolvedRecord;
    clientSettings: IClientSettings;
}
export interface IPmSessionStorageMap {
    templateResolvedRecord: ResolvedRecord;
    clientSettings: IClientSettings;
}

export interface IPpSessionStorageMap {
    templateResolvedRecord: never;
    clientSettings: PpClientSettings;
}
export interface ISnnPpSessionStorageMap {
    templateResolvedRecord: never;
    clientSettings: ISnnPpClientSettings;
}

export interface IPrePpWLSessionStorageMap {
    templateResolvedRecord: never;
    clientSettings: IPrePpWLClientSettings;
}
export interface IEnfPpoSessionStorageMap {
    templateResolvedRecord: never;
    clientSettings: IEnfPpoClientSettings;
}

export interface ISnnSessionStorageMap {
    templateResolvedRecord: ResolvedRecord;
    clientSettings: IClientSettings;
}
export interface IEhaSessionStorageMap {
    templateResolvedRecord: ResolvedRecord;
    clientSettings: IClientSettings;
}
export interface ITpoSessionStorageMap {
    templateResolvedRecord: ResolvedRecord;
    clientSettings: IClientSettings;
}

export interface ISnnPpClientSettings extends IClientSettings {
    DISCLAIMER: string;
    "logo.png"?: string;
    "logo.svg"?: string;
    HEADER_TEXT?: string;
    HEADER_URL?: string;
}
export interface IPrePpWLClientSettings extends IClientSettings {
    DISCLAIMER: string;
    "logo.png"?: string;
    "logo.svg"?: string;
    HEADER_TEXT?: string;
    HEADER_URL?: string;
    PAYMENT_FAKE_AMOUNT?: string | null;
}
export interface IEnfPpoClientSettings extends IClientSettings {
    DISCLAIMER: string;
    "logo.png"?: string;
    "logo.svg"?: string;
    HEADER_TEXT?: string;
    HEADER_URL?: string;
}

export interface PpClientSettings extends BaseClientSettings {
    MAP_CENTER_LAT: number;
    MAP_CENTER_LONG: number;
    LOCAL_GAZETTEER_URL: string;
    LOCAL_GAZETTEER_TOKEN: string;
    NATIONAL_GAZETTEER_URL: string;
    NATIONAL_GAZETTEER_TOKEN: string;
    HTML_TEMPLATE_TITLE: string;
    PICKLISTS: PicklistsFile;
    GRADIENT_LEFT_COLOR: string;
    GRADIENT_RIGHT_COLOR: string;
    CLIENT_WEBSITE_URL: string;
    ENABLE_LOGIN: boolean;
    BUILDING_REGULATIONS: boolean;
    COMPETENT_PERSONS_SCHEME: boolean;
    PLANNING_APPLICATIONS: boolean;
    PLANNING_APPEALS: boolean;
    WEEKLY_LISTS: boolean;
    ENFORCEMENT_NOTICES: boolean;
    ENFORCEMENT_APPEALS: boolean;
    PROPERTY_SEARCH_BUILDING_REGULATIONS: boolean;
    PROPERTY_SEARCH_COMPETENT_PERSONS_SCHEME: boolean;
    PROPERTY_SEARCH_PLANNING_APPLICATIONS: boolean;
    PROPERTY_SEARCH_PLANNING_APPEALS: boolean;
    PROPERTY_SEARCH_WEEKLY_LISTS: boolean;
    PROPERTY_SEARCH_ENFORCEMENT_NOTICES: boolean;
    PROPERTY_SEARCH_ENFORCEMENT_APPEALS: boolean;
    TERMS_AND_CONDITIONS: string;
    PLANNING_APPLICATION_RESULT_TABLE_COLUMNS: string[];
    DOCUMENTS_TABLE_COLUMNS: string[];
    HIDE_CONSTRAINTS_TAB: boolean;
    "logo.png": string;
    "logo.svg": string;
}

export interface ISessionStorageMap
    extends IBcSessionStorageMapper,
        ILcSessionStorageMapper,
        IPpSessionStorageMapper,
        ISnnPpSessionStorageMapper,
        IPmSessionStorageMapper,
        ISnnSessionStorageMapper,
        IEhaSessionStorageMapper,
        ITpoSessionStorageMapper,
        IPrePpWlSessionStorageMapper,
        IEnfPpoSessionStorageMapper {}

type IBcSessionStorageMapper = {
    [app in typeof MirageInternalApplicationTypes.buildingcontrol]: IBcSessionStorageMap;
};
type ILcSessionStorageMapper = {
    [app in typeof MirageInternalApplicationTypes.landcharges]: ILcSessionStorageMap;
};
type IPpSessionStorageMapper = {
    [app in typeof MirageApplicationTypes.publicportal]: IPpSessionStorageMap;
};
type IPrePpWlSessionStorageMapper = {
    [app in typeof MirageApplicationTypes.prePublicPortalWestLindsay]: IPrePpWLSessionStorageMap;
};
type IEnfPpoSessionStorageMapper = {
    [app in typeof MirageApplicationTypes.enforcementpublicportal]: IEnfPpoSessionStorageMap;
};
type ISnnPpSessionStorageMapper = {
    [app in typeof MirageApplicationTypes.streetNamingAndNumberingPublicPortal]: ISnnPpSessionStorageMap;
};
type IPmSessionStorageMapper = {
    [app in typeof MirageInternalApplicationTypes.planningmanagement]: IPmSessionStorageMap;
};
type ISnnSessionStorageMapper = {
    [app in typeof MirageInternalApplicationTypes.streetnamingandnumbering]: ISnnSessionStorageMap;
};
type IEhaSessionStorageMapper = {
    [app in typeof MirageInternalApplicationTypes.regulatoryservices]: IEhaSessionStorageMap;
};
type ITpoSessionStorageMapper = {
    [app in typeof MirageInternalApplicationTypes.treepreservationorder]: ITpoSessionStorageMap;
};

export default useSessionStorage;
